import axios from 'axios';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type { CreateRecipientPayload } from '../../document-view/recipients/types/create-recipient-payload';
import { ReminderMutations } from '../../document-view/graphql/mutations/reminder.mutations';
import { RecipientMutations } from '../graphql/recipient.mutations';
import { RecipientQueries } from '../graphql/recipient.queries';
import type { TransferSigningRightsArgs } from '../../document-view/recipients/types/transfer-signing-rights-args.ts';
import type { VideoReminderEventInput } from '../types/video-reminder-event-input';

export class RecipientService {
  static async getMany(documentId: string): Promise<Recipient[]> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: RecipientQueries.getMany,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return document.recipients;
  }

  static async updateRecipientOrder(documentId: string, recipientOrder: Partial<Recipient>[]) {
    const { data } = await apolloClientInternal.mutate({
      mutation: RecipientMutations.updateRecipientOrder,
      variables: {
        documentId,
        recipientOrder,
      },
    });
    return data;
  }

  static async create(documentId: string, recipient: Partial<Recipient>): Promise<Recipient> {
    const { data } = await apolloClientInternal.mutate({
      mutation: RecipientMutations.create,
      variables: { documentId, ...recipient },
    });
    return data.createRecipient;
  }

  static async createV2({ recipient, sendNow }: CreateRecipientPayload): Promise<Recipient> {
    const {
      data: { createRecipientV2 },
    } = await apolloClientInternal.mutate({
      mutation: RecipientMutations.createV2,
      variables: { recipient, sendNow },
    });

    return createRecipientV2;
  }

  static async transferSigningRights({
    documentId,
    recipientId,
    toRecipient,
  }: TransferSigningRightsArgs): Promise<Recipient> {
    const { data } = await apolloClientInternal.mutate({
      mutation: RecipientMutations.transferSigningRights,
      variables: { documentId, recipientId, toRecipient },
    });

    return data.transferRights;
  }

  static sendSmsReminder = async ({
    documentId,
    message,
    isNotOpenedSelected,
    isNotSignedSelected,
    recipientIds,
  }: {
    documentId: string;
    message: string;
    isNotSignedSelected: boolean;
    isNotOpenedSelected: boolean;
    recipientIds: string[];
  }): Promise<boolean> => {
    const {
      data: { sendSMSReminder },
    } = await apolloClientInternal.mutate({
      mutation: ReminderMutations.sms,
      variables: { documentId, message, isNotSignedSelected, isNotOpenedSelected, recipientIds },
    });
    return sendSMSReminder;
  };

  static sendEmailReminder = async (payload: {
    documentId: string;
    isNotSignedSelected: boolean;
    isNotOpenedSelected: boolean;
    recipientIds: string[];
  }): Promise<boolean> => {
    const {
      data: { sendEmailReminder },
    } = await apolloClientInternal.mutate({
      mutation: ReminderMutations.email,
      variables: {
        documentId: payload.documentId,
        isNotSignedSelected: payload.isNotSignedSelected,
        isNotOpenedSelected: payload.isNotOpenedSelected,
        recipientIds: payload.recipientIds,
      },
    });
    return sendEmailReminder;
  };

  static recallSigner = async ({
    documentId,
    recipientId,
  }: {
    documentId: string;
    recipientId: string;
  }): Promise<boolean> => {
    const {
      data: { recallRights },
    } = await apolloClientInternal.mutate({
      mutation: RecipientMutations.recallRights,
      variables: { documentId, recipientId },
    });
    return recallRights;
  };

  static async sendVideoReminder(
    documentId: string,
    payload: VideoReminderEventInput
  ): Promise<boolean> {
    const {
      data: { sendVideoReminder },
    } = await apolloClientInternal.mutate({
      mutation: ReminderMutations.sendVideoReminder,
      variables: {
        documentId,
        payload,
      },
    });
    return sendVideoReminder;
  }

  static async saveRecipient(documentId: string, recipient: Recipient): Promise<string> {
    const body: FormData = new FormData();
    body.append('action', 'save');
    body.append('recipient-id', recipient.id || '');
    body.append('recipient-first_name', recipient.firstName);
    body.append('recipient-last_name', recipient.lastName || '');
    body.append('recipient-role', recipient.role);
    body.append('recipient-email', recipient.email || '');
    body.append('recipient-company_name', recipient.companyName || '');
    body.append('recipient-company_number', recipient.companyNumber || '');
    body.append('recipient-title', recipient.title || '');
    body.append('recipient-mobile', recipient.mobile || '');
    body.append('recipient-note', recipient.note || '');
    body.append('recipient-thumb_url', recipient.thumbUrl || '');
    body.append('verify-eid', (+(recipient.verifyEid || '0')).toString());
    body.append('verify-eid-type', 'bankid');
    body.append('document-id', documentId);

    const { data } = await axios.post(`/save/recipient`, body);
    if (data.status === 1 || data.status === 2) {
      return data.recipient_id;
    }
    throw new Error(data.error);
  }

  static async update(documentId: string, recipient: Partial<Recipient>, sendNow?: boolean) {
    const {
      data: { updateRecipient },
    } = await apolloClientInternal.mutate({
      mutation: RecipientMutations.update,
      variables: {
        documentId,
        ...recipient,
        verifyEidType: recipient.verifyEidType === '' ? 'unknown' : recipient.verifyEidType,
        sendNow,
      },
    });
    return updateRecipient;
  }

  static async remove(id: string): Promise<boolean> {
    const { data } = await apolloClientInternal.mutate({
      mutation: RecipientMutations.delete,
      variables: { id },
    });
    return !!data;
  }

  static async addTemplateRole(payload: Recipient): Promise<string> {
    const body: FormData = new FormData();
    body.append('action', 'save');
    body.append('recipient-role_name', payload.roleName);
    body.append('recipient-role', payload.role);
    body.append('document-id', payload.documentId);
    body.append('verify-eid-type', 'bankid');
    body.append('verify-eid', (+(payload.verifyEid || '0')).toString());
    body.append('verify-sms', (+(payload.verifySms || '0')).toString());
    body.append('verify-sms-open', (+(payload.verifySmsOpen || '0')).toString());
    body.append('verify-sms-sign', (+(payload.verifySmsSign || '0')).toString());

    const { data } = await axios.post('/save/recipient', body);
    if (data.status === 1 || data.status === 2) {
      return data.recipient_id;
    }
    throw new Error(data.error);
  }

  static async updateRecipientThumbnail(file: File, email: string): Promise<string> {
    const body: FormData = new FormData();
    body.append('file', file);
    body.append('email', email || '');

    const { data } = await axios.post(`/save/recipient`, body);
    if (data.success) {
      const newThumbnailTimestamp = Math.round(new Date().getTime() / 1000) - 1;
      return `${data.image_url}?${newThumbnailTimestamp}`;
    }
    throw new Error(data.error);
  }
}
